import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { 
  Package2, Search, Plus, Edit, Trash2, X, Tag, Eye,
  TrendingUp, Sparkles, AlertCircle, CheckSquare, PackagePlus
} from 'lucide-react';
import LoadingSpinner from '../../../components/LoadingSpinner';
import { useToast } from '../../../hooks/useToast';
import { ToastContainer } from '../../../components/Toast';
import { packagesToAdd, addAllPackages } from '../../../utils/addPackages';
import { fixAllPackageSavings } from '../../../utils/fixPackageSavings';

interface ServicePackage {
  id?: string;
  name: string;
  description: string;
  serviceIds: string[];
  price: number;
  originalPrice?: number;
  discount?: number;
  savings: number;
  image: string;
  images: string[];
  popular?: boolean;
  featured?: boolean;
  new?: boolean;
  trending?: boolean;
  customTags?: string[];
  showInPopularSection?: boolean;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export default function PackagesView() {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [packages, setPackages] = useState<ServicePackage[]>([]);
  const [services, setServices] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingPackage, setEditingPackage] = useState<ServicePackage | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    serviceIds: [] as string[],
    price: '',
    originalPrice: '',
    discount: '',
    image: '',
    images: '',
    popular: false,
    featured: false,
    new: false,
    trending: false,
    customTags: [] as string[],
    showInPopularSection: false,
    isActive: true
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const packagesSnapshot = await getDocs(collection(db, 'packages'));
      const packagesData = packagesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...(doc.data() as Omit<ServicePackage, 'id'>)
      })) as ServicePackage[];
      setPackages(packagesData);
      
      const servicesSnapshot = await getDocs(collection(db, 'services'));
      const servicesData = servicesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setServices(servicesData);
    } catch (error) {
      console.error('Error loading data:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setError(`Failed to load data: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const calculateSavings = () => {
    if (!formData.serviceIds.length || !formData.price) return 0;
    
    const totalServicePrice = formData.serviceIds.reduce((sum, serviceId) => {
      const service = services.find(s => s.id === serviceId);
      const price = service?.price ?? 0;
      return sum + Number(price);
    }, 0);

    return Number(totalServicePrice) - parseFloat(formData.price);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || formData.serviceIds.length < 2 || !formData.price) {
      toast.warning('Please fill in all required fields and select at least 2 services');
      return;
    }

    try {
      setActionLoading(true);

      const savings = calculateSavings();
      
      // Build packageData object, excluding undefined fields (Firestore doesn't support undefined)
      const packageData: any = {
        name: formData.name,
        description: formData.description,
        serviceIds: formData.serviceIds,
        price: parseFloat(formData.price),
        savings,
        image: formData.image,
        images: formData.images ? formData.images.split('\n').filter(i => i.trim()) : [formData.image],
        popular: formData.popular,
        featured: formData.featured,
        new: formData.new,
        trending: formData.trending,
        customTags: formData.customTags,
        showInPopularSection: formData.showInPopularSection,
        isActive: formData.isActive,
        updatedAt: new Date().toISOString()
      };
      
      // Only add originalPrice and discount if they have values
      if (formData.originalPrice && formData.originalPrice.trim()) {
        packageData.originalPrice = parseFloat(formData.originalPrice);
      }
      if (formData.discount && formData.discount.trim()) {
        packageData.discount = parseInt(formData.discount);
      }

      if (editingPackage && editingPackage.id) {
        await updateDoc(doc(db, 'packages', editingPackage.id), packageData);
        toast.success('Package updated successfully!');
      } else {
        setError(null);
      await addDoc(collection(db, 'packages'), {
          ...packageData,
          createdAt: new Date().toISOString()
        });
        toast.success('Package added successfully!');
      }

      await loadData();
      setShowModal(false);
      resetForm();
    } catch (error) {
      console.error('Error saving package:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setError(`Failed to save package: ${errorMessage}`);
      toast.error(`Failed to save package: ${errorMessage}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleEdit = (pkg: ServicePackage) => {
    setEditingPackage(pkg);
    setFormData({
      name: pkg.name || '',
      description: pkg.description || '',
      serviceIds: pkg.serviceIds || [],
      price: pkg.price?.toString() || '',
      originalPrice: pkg.originalPrice?.toString() || '',
      discount: pkg.discount?.toString() || '',
      image: pkg.image || '',
      images: Array.isArray(pkg.images) ? pkg.images.join('\n') : '',
      popular: pkg.popular || false,
      featured: pkg.featured || false,
      new: pkg.new || false,
      trending: pkg.trending || false,
      customTags: pkg.customTags || [],
      showInPopularSection: pkg.showInPopularSection || false,
      isActive: pkg.isActive !== false
    });
    setShowModal(true);
  };

  const handleDelete = async (packageId: string | undefined) => {
    if (!packageId || !confirm('Are you sure you want to delete this package?')) {
      return;
    }

    try {
      setActionLoading(true);
      await deleteDoc(doc(db, 'packages', packageId));
      await loadData();
      setError(null);
      toast.success('Package deleted successfully!');
    } catch (error) {
      console.error('Error deleting package:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setError(`Failed to delete package: ${errorMessage}`);
      toast.error(`Failed to delete package: ${errorMessage}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddAllPackages = async () => {
    if (!confirm(`This will add ${packagesToAdd.length} packages to the database. Continue?`)) {
      return;
    }

    try {
      setActionLoading(true);
      toast.info('Adding packages... Please wait');
      
      const result = await addAllPackages();
      
      if (result.success) {
        await loadData();
        toast.success(`Successfully added ${result.added} packages! (Skipped: ${result.skipped}, Errors: ${result.errors || 0})`);
      } else {
        toast.error('Failed to add packages. Check console for details.');
      }
    } catch (error) {
      console.error('Error in bulk add:', error);
      toast.error(`Error adding packages: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleFixPackageSavings = async () => {
    if (!confirm('This will recalculate and update savings for all packages in the database. Continue?')) {
      return;
    }

    try {
      setActionLoading(true);
      toast.info('Fixing package savings... Please wait');
      
      const result = await fixAllPackageSavings();
      
      if (result.success) {
        await loadData();
        toast.success(`Successfully updated ${result.updated} packages! Check console for details.`);
      } else {
        toast.error('Failed to fix package savings. Check console for details.');
      }
    } catch (error) {
      console.error('Error fixing savings:', error);
      toast.error(`Error fixing savings: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setActionLoading(false);
    }
  };

  const resetForm = () => {
    setEditingPackage(null);
    setFormData({
      name: '',
      description: '',
      serviceIds: [],
      price: '',
      originalPrice: '',
      discount: '',
      image: '',
      images: '',
      popular: false,
      featured: false,
      new: false,
      trending: false,
      customTags: [],
      showInPopularSection: false,
      isActive: true
    });
  };

  const toggleServiceSelection = (serviceId: string) => {
    setFormData(prev => ({
      ...prev,
      serviceIds: prev.serviceIds.includes(serviceId)
        ? prev.serviceIds.filter(id => id !== serviceId)
        : [...prev.serviceIds, serviceId]
    }));
  };

  const filteredPackages = packages.filter(pkg =>
    pkg.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    pkg.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <ToastContainer toasts={toast.toasts} removeToast={toast.removeToast} />
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-gradient-to-r from-pink-50 to-purple-50 p-6 rounded-xl border border-pink-100">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <div className="p-2 bg-gradient-to-r from-pink-600 to-purple-600 rounded-lg">
              <Package2 className="h-7 w-7 text-white" />
            </div>
            Service Packages
          </h1>
          <p className="text-gray-600 mt-2 ml-14">Bundle multiple services and offer special deals</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleFixPackageSavings}
            disabled={actionLoading}
            className="px-4 py-3 bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-xl hover:from-orange-700 hover:to-red-700 font-semibold flex items-center gap-2 shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            title="Recalculate savings for all packages"
          >
            <Sparkles className="h-5 w-5" />
            Fix Savings
          </button>
          <button
            onClick={handleAddAllPackages}
            disabled={actionLoading}
            className="px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:from-green-700 hover:to-emerald-700 font-semibold flex items-center gap-2 shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <PackagePlus className="h-5 w-5" />
            Add 5 Packages
          </button>
          <button
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="px-6 py-3 bg-gradient-to-r from-pink-600 to-purple-600 text-white rounded-xl hover:from-pink-700 hover:to-purple-700 font-semibold flex items-center gap-2 shadow-lg hover:shadow-xl transition-all"
          >
            <Plus className="h-5 w-5" />
            Add Package
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-xl flex items-start gap-3">
          <AlertCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-semibold">Error</h3>
            <p className="text-sm mt-1">{error}</p>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
        <input
          type="text"
          placeholder="Search packages by name or description..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-pink-500 transition-all"
        />
      </div>

      {/* Packages Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gradient-to-r from-pink-50 to-purple-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Package
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Services
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Price
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Savings
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Tags
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-4 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {filteredPackages.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12">
                    <div className="text-center">
                      <Package2 className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No packages found</h3>
                      <p className="text-gray-600">
                        {searchTerm ? 'Try adjusting your search' : 'Click "Add Package" to create service bundles'}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredPackages.map((pkg) => (
                  <motion.tr
                    key={pkg.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="hover:bg-pink-50/30 transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-4">
                        {pkg.image && (
                          <img
                            src={pkg.image}
                            alt={pkg.name}
                            className="h-14 w-14 rounded-xl object-cover ring-2 ring-pink-100"
                          />
                        )}
                        <div className="flex-1">
                          <div className="font-semibold text-gray-900 flex items-center gap-2">
                            {pkg.name}
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r from-pink-100 to-purple-100 text-pink-700">
                              Package
                            </span>
                          </div>
                          <div className="text-sm text-gray-500 line-clamp-1 mt-0.5">{pkg.description}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <CheckSquare className="h-4 w-4 text-purple-500" />
                        <span className="text-sm font-medium text-gray-900">
                          {pkg.serviceIds?.length || 0} services
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        <div className="flex items-center gap-1 text-sm font-bold text-pink-700">
                          ₹{pkg.price}
                        </div>
                        {pkg.originalPrice && (
                          <div className="text-xs text-gray-500 line-through">₹{pkg.originalPrice}</div>
                        )}
                        {pkg.discount && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                            {pkg.discount}% OFF
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-green-100 to-emerald-100">
                        <Sparkles className="h-3.5 w-3.5 text-green-600" />
                        <span className="text-sm font-bold text-green-700">
                          ₹{Number(pkg.savings || 0).toLocaleString('en-IN')}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1.5">
                        {pkg.popular && (
                          <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-yellow-100 text-yellow-700">
                            <TrendingUp className="h-3 w-3 mr-1" />
                            Popular
                          </span>
                        )}
                        {pkg.featured && (
                          <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-purple-100 text-purple-700">
                            <Sparkles className="h-3 w-3 mr-1" />
                            Featured
                          </span>
                        )}
                        {pkg.new && (
                          <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-green-100 text-green-700">
                            <AlertCircle className="h-3 w-3 mr-1" />
                            New
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                        pkg.isActive !== false 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        <Eye className="h-3 w-3 mr-1" />
                        {pkg.isActive !== false ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleEdit(pkg)}
                          className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                          title="Edit package"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(pkg.id)}
                          className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                          title="Delete package"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
            >
              <div className="flex items-center justify-between p-6 border-b">
                <h2 className="text-2xl font-bold text-gray-900">
                  {editingPackage ? 'Edit Package' : 'Add New Package'}
                </h2>
                <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="overflow-y-auto" style={{ maxHeight: 'calc(90vh - 180px)' }}>
                <div className="p-6 space-y-6">
                  {/* Basic Info */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-gray-900">Basic Information</h3>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Package Name *</label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="e.g., Ultimate Beauty Package"
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        rows={3}
                        placeholder="Describe what's included in this package"
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                        required
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Package Price (₹) *</label>
                        <input
                          type="number"
                          value={formData.price}
                          onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Original Price (₹)</label>
                        <input
                          type="number"
                          value={formData.originalPrice}
                          onChange={(e) => setFormData({ ...formData, originalPrice: e.target.value })}
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                    </div>

                    {/* Savings Info */}
                    {formData.serviceIds.length >= 2 && formData.price && (
                      <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                        <div className="text-sm font-medium text-green-900">
                          Estimated Savings: ₹{calculateSavings()}
                        </div>
                        <div className="text-xs text-green-700 mt-1">
                          Based on individual service prices vs package price
                        </div>
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Main Image URL *</label>
                      <input
                        type="url"
                        value={formData.image}
                        onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                        placeholder="https://example.com/image.jpg"
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Additional Images (one per line)</label>
                      <textarea
                        value={formData.images}
                        onChange={(e) => setFormData({ ...formData, images: e.target.value })}
                        rows={3}
                        placeholder="https://example.com/image1.jpg&#10;https://example.com/image2.jpg"
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                  </div>

                  {/* Services Selection */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-gray-900">
                      Select Services (minimum 2) *
                    </h3>
                    
                    <div className="max-h-64 overflow-y-auto border rounded-lg">
                      {services.map(service => (
                        <label
                          key={service.id}
                          className={`flex items-center p-3 cursor-pointer border-b hover:bg-gray-50 ${
                            formData.serviceIds.includes(service.id) ? 'bg-primary-50' : ''
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={formData.serviceIds.includes(service.id)}
                            onChange={() => toggleServiceSelection(service.id)}
                            className="rounded text-primary-600 focus:ring-primary-500"
                          />
                          <div className="ml-3 flex-1">
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="text-sm font-medium text-gray-900">{service.name}</div>
                                <div className="text-xs text-gray-500">
                                  {service.category || service.categoryId || 'No category'}
                                </div>
                              </div>
                              <div className="text-right flex flex-col items-end">
                                {service.originalPrice ? (
                                  <>
                                    <div className="text-sm font-semibold text-pink-600">₹{service.price}</div>
                                    <div className="flex items-center gap-1">
                                      <span className="text-xs text-gray-500 line-through">₹{service.originalPrice}</span>
                                      {service.discount && (
                                        <span className="text-xs font-medium text-green-600 bg-green-50 px-1.5 py-0.5 rounded">
                                          {service.discount}% off
                                        </span>
                                      )}
                                    </div>
                                  </>
                                ) : (
                                  <div className="text-sm font-semibold text-gray-900">₹{service.price}</div>
                                )}
                              </div>
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                    
                    <div className="text-sm text-gray-600">
                      {formData.serviceIds.length} service(s) selected
                    </div>
                  </div>

                  {/* Tags */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-gray-900">Package Tags</h3>
                    <div className="grid grid-cols-2 gap-4">
                      {[
                        { key: 'popular', label: 'Popular', icon: TrendingUp },
                        { key: 'featured', label: 'Featured', icon: Sparkles },
                        { key: 'new', label: 'New', icon: AlertCircle },
                        { key: 'trending', label: 'Trending', icon: TrendingUp }
                      ].map(tag => (
                        <label key={tag.key} className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData[tag.key as keyof typeof formData] as boolean}
                            onChange={(e) => setFormData({ ...formData, [tag.key]: e.target.checked })}
                            className="rounded text-primary-600 focus:ring-primary-500"
                          />
                          <tag.icon className="h-4 w-4 text-gray-500" />
                          <span className="text-sm text-gray-700">{tag.label}</span>
                        </label>
                      ))}
                    </div>

                    {/* Custom Tags */}
                    <div className="mt-4 space-y-2">
                      <label className="block text-sm font-medium text-gray-700">Custom Tags</label>
                      <div className="flex flex-wrap gap-2">
                        {formData.customTags.map((tag, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-sm bg-gray-100 text-gray-800"
                          >
                            <Tag className="h-3.5 w-3.5" />
                            {tag}
                            <button
                              type="button"
                              onClick={() => {
                                const newTags = [...formData.customTags];
                                newTags.splice(index, 1);
                                setFormData({ ...formData, customTags: newTags });
                              }}
                              className="p-0.5 hover:bg-gray-200 rounded-full"
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </span>
                        ))}
                        <input
                          type="text"
                          placeholder="Add a tag..."
                          className="px-3 py-1.5 text-sm border rounded-full focus:ring-2 focus:ring-primary-500"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              const input = e.currentTarget;
                              const value = input.value.trim();
                              if (value && !formData.customTags.includes(value)) {
                                setFormData({
                                  ...formData,
                                  customTags: [...formData.customTags, value]
                                });
                                input.value = '';
                              }
                            }
                          }}
                        />
                      </div>
                      <p className="text-xs text-gray-500">
                        Press Enter to add a tag
                      </p>
                    </div>
                  </div>

                  {/* Display Options */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-gray-900">Visibility & Display</h3>
                    
                    {/* Package Visibility - Most Important */}
                    <div className={`p-4 rounded-lg border-2 ${formData.isActive ? 'bg-green-50 border-green-300' : 'bg-red-50 border-red-300'}`}>
                      <label className="flex items-start space-x-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.isActive}
                          onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                          className="mt-1 w-5 h-5 rounded text-primary-600 focus:ring-primary-500"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Eye className={`h-5 w-5 ${formData.isActive ? 'text-green-600' : 'text-red-600'}`} />
                            <span className={`font-semibold ${formData.isActive ? 'text-green-900' : 'text-red-900'}`}>
                              Package is {formData.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                          <p className={`text-sm mt-1 ${formData.isActive ? 'text-green-700' : 'text-red-700'}`}>
                            {formData.isActive 
                              ? 'This package is visible to customers and can be purchased' 
                              : 'This package is hidden from customers and cannot be purchased'}
                          </p>
                        </div>
                      </label>
                    </div>

                    {/* Other Display Options */}
                    <div className="space-y-2 pt-2 border-t border-gray-200">
                      <p className="text-sm text-gray-600 mb-3">Homepage Display Settings</p>
                      <label className="flex items-center space-x-2 cursor-pointer p-2 hover:bg-gray-50 rounded">
                        <input
                          type="checkbox"
                          checked={formData.showInPopularSection}
                          onChange={(e) => setFormData({ ...formData, showInPopularSection: e.target.checked })}
                          className="rounded text-primary-600 focus:ring-primary-500"
                        />
                        <Package2 className="h-4 w-4 text-gray-500" />
                        <span className="text-sm text-gray-700">Show in Premium Section</span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="flex justify-end space-x-3 px-6 py-4 border-t bg-gray-50">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 text-gray-700 hover:text-gray-900"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={actionLoading}
                    className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                  >
                    {actionLoading ? 'Saving...' : editingPackage ? 'Update Package' : 'Create Package'}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
